import React, { useState, useEffect } from 'react';
import DashboardLayout from "../layouts/DashboardLayout";
import InfoModal from "../layouts/InfoModal";
import PaginatedTableContainer from "../containers/PaginatedTableContainer";
import { 
    getAllRiceTransactions, 
    searchRiceTransactions,
    syncRiceTransactions
} from "../../api/AdminApi";

const RiceTransactionManagementPage = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    
    // 검색 관련 상태
    const [searchQuery, setSearchQuery] = useState('');
    const [searchField, setSearchField] = useState('id');
    const [isSearchMode, setIsSearchMode] = useState(false);

    // 모달 관련 상태
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);

    const riceTransactionReasonOptions = [
        { value: 'SIGNUP', label: 'SIGNUP - 최초 가입' },
        { value: 'MEETING_APPLY', label: 'MEETING_APPLY - 모임 신청' },
        { value: 'MEETING_APPLY_REJECT', label: 'MEETING_APPLY_REJECT - 모임 신청 거절' },
        { value: 'MEETING_APPLY_DELETE', label: 'MEETING_APPLY_DELETE - 모임 신청 삭제' },
        { value: 'MEETING_CREATE', label: 'MEETING_CREATE - 모임 생성' },
        { value: 'MEETING_JOIN', label: 'MEETING_JOIN - 모임 참여' },
        { value: 'MEETING_CHECKED', label: 'MEETING_CHECKED - 모임 출석체크' },
        { value: 'MEETING_DELETE', label: 'MEETING_DELETE - 모임 삭제' },
        { value: 'MEETING_END', label: 'MEETING_END - 모임 종료(충족X)' },
        { value: 'MEETING_LEAVE', label: 'MEETING_LEAVE - 모임 모집 중 나감' },
        { value: 'PURCHASE_500', label: 'PURCHASE_500 - 쌀 500개 구입' },
        { value: 'PURCHASE_1000', label: 'PURCHASE_1000 - 쌀 1000개 구입' },
        { value: 'PURCHASE_2000', label: 'PURCHASE_2000 - 쌀 2000개 구입' },
        { value: 'ADMIN_GRANT', label: 'ADMIN_GRANT - 어드민 지급' },
        { value: 'ADMIN_REVOKE', label: 'ADMIN_REVOKE - 어드민 회수' }
    ];

    const fetchTransactions = async (page = 0) => {
        setLoading(true);
        try {
            const response = await getAllRiceTransactions(page, 20);
            setTransactions(response.content);
            setCurrentPage(response.currentPage);
            setTotalPages(response.totalPages);
            setTotalElements(response.totalElements);
            setIsSearchMode(false);
        } catch (error) {
            console.error('쌀 트랜잭션 목록 조회 실패:', error);
            alert('쌀 트랜잭션 목록을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (page = 0) => {
        if (!searchQuery.trim()) {
            alert('검색어를 입력해주세요.');
            return;
        }
        
        setLoading(true);
        try {
            const response = await searchRiceTransactions(page, 20, searchQuery, searchField);
            setTransactions(response.content);
            setCurrentPage(response.currentPage);
            setTotalPages(response.totalPages);
            setTotalElements(response.totalElements);
            setIsSearchMode(true);
        } catch (error) {
            console.error('쌀 트랜잭션 검색 실패:', error);
            alert('쌀 트랜잭션 검색에 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleRowClick = async (transaction) => {
        setModalLoading(true);
        setModalOpen(true);
        try {
            setSelectedTransaction(transaction);
        } catch (error) {
            console.error('쌀 트랜잭션 상세 조회 실패:', error);
            alert('쌀 트랜잭션 정보를 불러오는데 실패했습니다.');
            setModalOpen(false);
        } finally {
            setModalLoading(false);
        }
    };

    const handleSyncTransactions = async () => {
        const confirmed = window.confirm('쌀 트랜잭션을 동기화하시겠습니까? 이 작업은 시간이 걸릴 수 있습니다.');
        if (!confirmed) return;

        try {
            await syncRiceTransactions();
            alert('쌀 트랜잭션 동기화가 완료되었습니다.');
            // 목록 새로고침
            fetchTransactions(currentPage);
        } catch (error) {
            console.error('쌀 트랜잭션 동기화 실패:', error);
            alert('쌀 트랜잭션 동기화에 실패했습니다.');
        }
    };

    useEffect(() => {
        fetchTransactions();
    }, []);

    const handlePageChange = (page) => {
        if (page >= 0 && page < totalPages) {
            setCurrentPage(page);
            if (isSearchMode && searchQuery.trim()) {
                handleSearch(page);
            } else {
                fetchTransactions(page);
            }
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleString('ko-KR');
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(0);
        handleSearch(0);
    };

    const handleReset = () => {
        setSearchQuery('');
        setCurrentPage(0);
        fetchTransactions(0);
    };

    // 테이블 컬럼 정의
    const columns = [
        { header: 'id', field: 'id', minWidth: 'min-w-12' },
        { header: 'memberId', field: 'memberId', minWidth: 'min-w-16' },
        { 
            header: 'amount', 
            field: 'amount', 
            minWidth: 'min-w-12',
            render: (transaction) => (
                <span className={transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {transaction.amount >= 0 ? `+${transaction.amount}` : transaction.amount}
                </span>
            )
        },
        { header: 'riceTransactionReason', field: 'riceTransactionReason', minWidth: 'min-w-32' },
        { header: 'balanceAfter', field: 'balanceAfter', minWidth: 'min-w-16' },
        { 
            header: 'createdAt', 
            field: 'createdAt', 
            minWidth: 'min-w-32',
            render: (transaction) => formatDateTime(transaction.createdAt)
        }
    ];

    return (
        <DashboardLayout>
            <div className="p-3">
                <div className="flex items-center justify-between mb-3">
                    <h1 className="text-lg font-bold">쌀 트랜잭션 관리</h1>
                    <button 
                        onClick={handleSyncTransactions}
                        className="px-3 py-1 bg-blue-200 border border-gray-300 hover:bg-blue-300 text-xs"
                    >
                        트랜잭션 동기화
                    </button>
                </div>
                
                {/* 검색 폼 */}
                <div className="mb-3 p-2 bg-gray-100 border border-gray-300">
                    <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 text-xs">
                        <div className="flex gap-2">
                            <label className="flex items-center">
                                <input 
                                    type="radio" 
                                    name="searchField" 
                                    value="id" 
                                    checked={searchField === 'id'}
                                    onChange={(e) => setSearchField(e.target.value)}
                                    className="mr-1"
                                />
                                ID
                            </label>
                            <label className="flex items-center">
                                <input 
                                    type="radio" 
                                    name="searchField" 
                                    value="member_id" 
                                    checked={searchField === 'member_id'}
                                    onChange={(e) => setSearchField(e.target.value)}
                                    className="mr-1"
                                />
                                멤버ID
                            </label>
                            <label className="flex items-center">
                                <input 
                                    type="radio" 
                                    name="searchField" 
                                    value="rice_transaction_reason" 
                                    checked={searchField === 'rice_transaction_reason'}
                                    onChange={(e) => setSearchField(e.target.value)}
                                    className="mr-1"
                                />
                                트랜잭션 사유
                            </label>
                        </div>
                        {searchField === 'rice_transaction_reason' ? (
                            <select 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="px-2 py-1 border border-gray-300 text-xs"
                            >
                                <option value="">사유 선택</option>
                                {riceTransactionReasonOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <input 
                                type="text" 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="검색어 입력"
                                className="px-2 py-1 border border-gray-300 text-xs"
                            />
                        )}
                        <button type="submit" className="px-2 py-1 bg-gray-200 border border-gray-300 hover:bg-gray-300 text-xs">
                            검색
                        </button>
                        <button type="button" onClick={handleReset} className="px-2 py-1 bg-gray-200 border border-gray-300 hover:bg-gray-300 text-xs">
                            전체보기
                        </button>
                    </form>
                </div>

                {/* 상단 정보 */}
                <div className="mb-2 text-xs text-gray-600">
                    총 {totalElements}건 | {currentPage + 1}/{totalPages} 페이지 
                    {isSearchMode && ` | 검색: ${searchField}="${searchQuery}"`}
                </div>

                {/* 페이지네이션 테이블 */}
                <PaginatedTableContainer
                    columns={columns}
                    data={transactions}
                    loading={loading}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    onRowClick={handleRowClick}
                />

                {/* 쌀 트랜잭션 상세 모달 */}
                <InfoModal 
                    isOpen={modalOpen} 
                    onClose={() => setModalOpen(false)} 
                    title={`쌀 트랜잭션 정보 - ID: ${selectedTransaction?.id || ''}`}
                    width="w-[600px]"
                >
                    {modalLoading ? (
                        <div className="text-center py-4">쌀 트랜잭션 정보를 불러오는 중...</div>
                    ) : selectedTransaction ? (
                        <div className="text-xs">
                            <h3 className="font-bold mb-2 text-sm">쌀 트랜잭션 정보</h3>
                            <table className="w-full border border-gray-400">
                                <tbody>
                                    <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100 w-24">id</td><td className="border border-gray-400 px-2 py-1">{selectedTransaction.id}</td></tr>
                                    <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">memberId</td><td className="border border-gray-400 px-2 py-1">{selectedTransaction.memberId}</td></tr>
                                    <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">amount</td><td className="border border-gray-400 px-2 py-1">
                                        <span className={selectedTransaction.amount >= 0 ? 'text-green-600' : 'text-red-600'}>
                                            {selectedTransaction.amount >= 0 ? `+${selectedTransaction.amount}` : selectedTransaction.amount}
                                        </span>
                                    </td></tr>
                                    <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">riceTransactionReason</td><td className="border border-gray-400 px-2 py-1">{selectedTransaction.riceTransactionReason}</td></tr>
                                    <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">balanceAfter</td><td className="border border-gray-400 px-2 py-1">{selectedTransaction.balanceAfter}</td></tr>
                                    <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">createdAt</td><td className="border border-gray-400 px-2 py-1">{formatDateTime(selectedTransaction.createdAt)}</td></tr>
                                </tbody>
                            </table>
                        </div>
                    ) : null}
                </InfoModal>
            </div>
        </DashboardLayout>
    );
};

export default RiceTransactionManagementPage;